import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, Loader2, ArrowUpRight, FileText, Download, Upload, CheckCircle2, LogOut, Clock } from "lucide-react";
import Seo from "../components/site/Seo";
import { SectionLabel } from "../components/site/Section";
import { Reveal } from "../components/site/Reveal";
import {
  candidateLogin, candidateMe, candidateForms, candidateSubmitForm,
  candidateDownload, candidateChangePassword, getCandToken, setCandToken, apiError,
} from "../lib/api";
import SetPasswordDialog from "../components/site/SetPasswordDialog";
import ForgotPasswordDialog from "../components/site/ForgotPasswordDialog";

const FIELD = "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";

export default function CandidatePortal() {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  useEffect(() => {
    if (!getCandToken()) return setUser(false);
    candidateMe().then(setUser).catch(() => { setCandToken(null); setUser(false); });
  }, []);

  if (user === null) return <div className="grid min-h-screen place-items-center bg-background"><Loader2 className="animate-spin text-brand" /></div>;
  return user ? <Dashboard user={user} onLogout={() => { setCandToken(null); setUser(false); }} /> : <CandidateLogin onAuthed={setUser} />;
}

function CandidateLogin({ onAuthed }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("Enter your email and password.");
    setLoading(true);
    try {
      const data = await candidateLogin(form.email, form.password);
      if (data.user.role !== "candidate") { setCandToken(null); return toast.error("This portal is for candidates. Please use the correct login."); }
      toast.success("Welcome back.");
      onAuthed(data.user);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="candidate-portal-page">
      <Seo title="Candidate Portal" description="Secure candidate portal for VectorBridge Group — complete and upload your HR onboarding forms." />
      <section className="relative min-h-[100svh] pt-32">
        <div className="tech-grid pointer-events-none absolute inset-0 opacity-20 mask-fade-b" />
        <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 py-16 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionLabel index="P/01">Candidate Portal</SectionLabel>
            <Reveal><h1 className="mt-8 font-display text-5xl font-light leading-[0.95] tracking-tighter sm:text-6xl">Your onboarding, <span className="text-brand">in one place.</span></h1></Reveal>
            <Reveal delay={0.1}><p className="mt-6 max-w-md text-muted-foreground">Log in to access, complete, and upload the HR and onboarding forms assigned to you by our team.</p></Reveal>
            <ul className="mt-10 divide-y divide-border border-t border-border">
              {["Download assigned HR forms", "Upload your completed documents", "Track submission status in real time"].map((b, i) => (
                <Reveal key={b} delay={0.1 + i * 0.05}><li className="flex items-center gap-3 py-4 text-sm text-muted-foreground"><ShieldCheck size={16} className="text-brand" /> {b}</li></Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-border bg-card/60 p-6 backdrop-blur-xl sm:p-10">
              <div className="flex items-center gap-2 text-brand"><Lock size={16} /> <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Encrypted Access</span></div>
              <form onSubmit={submit} data-testid="candidate-login-form" className="mt-6 space-y-4">
                <input name="email" type="email" value={form.email} onChange={change} placeholder="Work email" className={FIELD} data-testid="candidate-email" />
                <input name="password" type="password" value={form.password} onChange={change} placeholder="Password" className={FIELD} data-testid="candidate-password" />
                <button type="submit" disabled={loading} data-testid="candidate-login-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />} Enter portal
                </button>
              </form>
              <p className="mt-6 text-center text-xs text-muted-foreground">Need access? Your VectorBridge recruiter will set up your account. <Link to="/careers" className="text-brand link-underline">View openings</Link></p>
              <button type="button" onClick={() => setForgot(true)} data-testid="candidate-forgot-password" className="mt-4 block w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-brand">Forgot password?</button>
              <ForgotPasswordDialog open={forgot} onOpenChange={setForgot} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [forms, setForms] = useState(null);
  const [mustChange, setMustChange] = useState(!!user.must_change_password);
  const load = () => candidateForms().then(setForms).catch(() => setForms([]));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="candidate-dashboard" className="min-h-screen bg-background">
      <SetPasswordDialog open={mustChange} onSubmit={async (pw) => { await candidateChangePassword(pw); setMustChange(false); }} />
      <header className="border-b border-border pt-28 pb-10">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-end justify-between gap-4 px-5 sm:px-8">
          <div>
            <SectionLabel index="P/01">Candidate Portal</SectionLabel>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tighter sm:text-5xl">Welcome, {user.name?.split(" ")[0] || "Candidate"}.</h1>
            <p className="mt-2 text-sm text-muted-foreground">{user.email}</p>
          </div>
          <button onClick={onLogout} data-testid="candidate-logout" className="inline-flex items-center gap-2 border border-border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-12 sm:px-8">
        <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
          <span className="text-brand">HR</span><span className="h-px w-8 bg-border" /> Assigned Forms
        </div>
        {forms === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : forms.length === 0 ? (
          <div className="mt-10 border border-dashed border-border p-12 text-center">
            <FileText size={32} className="mx-auto text-muted-foreground" />
            <p className="mt-4 font-display text-2xl tracking-tight">No forms assigned yet.</p>
            <p className="mt-2 text-muted-foreground">When our team assigns onboarding documents, they'll appear here.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {forms.map((f) => <FormCard key={f.id} form={f} onChanged={load} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function FormCard({ form, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const submitted = form.status === "submitted";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) return toast.error("Upload a PDF, DOC, or DOCX file.");
    if (file.size > 10 * 1024 * 1024) return toast.error("File must be under 10MB.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await candidateSubmitForm(form.id, fd);
      toast.success("Completed form uploaded.");
      onChanged();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid={`candidate-form-${form.id}`} className="border border-border bg-card/40 p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-display text-2xl tracking-tight">{form.title}</h3>
            <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] ${submitted ? "text-brand" : "text-yellow-500"}`}>
              {submitted ? <CheckCircle2 size={13} /> : <Clock size={13} />} {submitted ? "Submitted" : "Action needed"}
            </span>
          </div>
          {form.description && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{form.description}</p>}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={() => candidateDownload(form.id, "blank", form.blank?.original_filename)} data-testid={`candidate-download-blank-${form.id}`} className="inline-flex items-center gap-2 border border-border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"><Download size={14} /> Download form</button>

        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`candidate-upload-${form.id}`} className="inline-flex items-center gap-2 bg-brand px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {submitted ? "Replace upload" : "Upload completed"}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" data-testid={`candidate-upload-input-${form.id}`} />

        {submitted && (
          <button onClick={() => candidateDownload(form.id, "completed", form.completed?.original_filename)} data-testid={`candidate-download-completed-${form.id}`} className="inline-flex items-center gap-2 border border-border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground"><FileText size={14} /> Your submission</button>
        )}
      </div>
    </div>
  );
}
